import { IUserObject } from 'types'
import { safeLocalStorage } from 'helpers/localStorage'

export const PERMISSIONS = {
  viewer: 0,
  attentionOf: 1,
  contributor: 2,
  reviewer: 3,
  owner: 4
}

const AUTH_KEY = 'morta_user'
const MASQUERADE_KEY = 'morta_masquerade'

export const getAuthToken = () => {
  const currentUser = getCurrentUser()
  if (getMasqueradeToken()) {
    return `BearerMasquerade ${getMasqueradeToken()}`
  } else if (currentUser) {
    return `Bearer ${currentUser.authToken}`
  }
}

export const getCurrentUser = (): IUserObject | undefined => {
  const data = safeLocalStorage.getItem(AUTH_KEY)
  if (data !== null) {
    return JSON.parse(data)
  }
}

export const getMasqueradeToken = () => {
  return safeLocalStorage.getItem(MASQUERADE_KEY)
}

// Fired on `window` when the masquerade token is set or cleared, since that
// changes getAuthToken() without touching any React state (the websocket
// reconnects on it, see hooks/useSocketCredentialSync).
export const MASQUERADE_CHANGED_EVENT = 'morta:masquerade-changed'

const announceMasqueradeChange = (): void => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(MASQUERADE_CHANGED_EVENT))
}

export const setMasqueradeToken = (token: string) => {
  safeLocalStorage.setItem(MASQUERADE_KEY, token)
  announceMasqueradeChange()
}

export const clearMasqueradeToken = () => {
  safeLocalStorage.removeItem(MASQUERADE_KEY)
  announceMasqueradeChange()
}

// Module-level latch that survives microtask boundaries within the same JS
// context. Set when the session is forcibly ended (401 handler, logout) so
// in-flight API responses that resolve after that point cannot rewrite
// AUTH_KEY via updateUser and resurrect a dead session. Reset by a fresh
// login. The latch is naturally cleared by the hard reload in
// handleTokenExpired, so it only matters during the brief race window.
let loggedOutFlag = false

export const markLoggedOut = (): void => {
  loggedOutFlag = true
}

export const clearLoggedOutFlag = (): void => {
  loggedOutFlag = false
}

export const hasBeenLoggedOut = (): boolean => loggedOutFlag

export const getSessionExpiryInfo = (
  user?: IUserObject | undefined
): { secToExp: number; isExpired: boolean; isInvalid: boolean } | null => {
  const currentUser = user ?? getCurrentUser()
  if (!currentUser || !currentUser.authTokenExpiresAt) {
    return null
  }

  const expiryMs = new Date(currentUser.authTokenExpiresAt).getTime()
  if (Number.isNaN(expiryMs)) {
    return { secToExp: 0, isExpired: true, isInvalid: true }
  }

  const now = Date.now()
  const secToExp = (expiryMs - now) / 1000

  return {
    secToExp,
    isExpired: secToExp <= 0,
    isInvalid: false
  }
}

/**
 * Checks if the current session is expired.
 * Uses the same logic as session.tsx to determine expiry.
 * @param user - Optional user object from useAuth. If not provided, uses getCurrentUser()
 */
export const isSessionExpired = (user?: IUserObject | undefined): boolean => {
  const info = getSessionExpiryInfo(user)
  return info?.isExpired ?? false
}
